import React, { ReactNode } from 'react'
import { Alert, Box, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { C } from './tokens'

// Peças de texto dos cadastros em etapas. Vieram do desconto v2 quando a campanha de
// recuperação precisou das mesmas; `Rich` ficou lá, porque depende do parseRich do modelo
// daquele cadastro.

type Tone = 'info' | 'warn' | 'error' | 'success'
const SEVERITY = { info: 'info', warn: 'warning', error: 'error', success: 'success' } as const

/** Aviso colorido (as mesmas cores do Alert do MUI). `role="note"` por padrão: só quem avisa de erro anuncia. */
export const Callout: React.FC<{
  tone: Tone
  children: ReactNode
  role?: string
  id?: string
  icon?: ReactNode
  sx?: SxProps<Theme>
  tabIndex?: number
}> = ({ tone, children, role = 'note', id, icon, sx, tabIndex }) => (
  <Alert
    id={id}
    severity={SEVERITY[tone]}
    role={role}
    icon={icon}
    tabIndex={tabIndex}
    sx={{
      borderRadius: '10px', py: 1.25, px: 1.75, fontSize: 14, lineHeight: 1.5, alignItems: 'flex-start', outline: 'none',
      '& .MuiAlert-icon': { mt: '1px', py: '2px', mr: 1.25 },
      '& .MuiAlert-message': { py: '2px', minWidth: 0, overflowWrap: 'anywhere' },
      ...(sx as object),
    }}
  >
    {children}
  </Alert>
)

/** Mensagem de erro do campo (o `id` entra no aria-describedby do campo). */
export const FieldError: React.FC<{ id: string; children: ReactNode }> = ({ id, children }) => (
  <Box id={id} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', fontSize: 13, color: C.red, lineHeight: 1.4 }}>
    <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />
    <span>{children}</span>
  </Box>
)

/** Título de uma pergunta dentro da etapa, com ajuda curta. */
export const Ask: React.FC<{ id: string; wrapId?: string; title: ReactNode; help?: ReactNode; children?: ReactNode }> = ({ id, wrapId, title, help, children }) => (
  <Box id={wrapId} component="section" aria-labelledby={id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography id={id} component="h3" sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</Typography>
      {help ? <Box component="span" sx={{ fontSize: 13, color: C.muted, lineHeight: 1.45 }}>{help}</Box> : null}
    </Box>
    {children}
  </Box>
)

/** Frase pequena de apoio. */
export const Hint: React.FC<{ id?: string; children: ReactNode; sx?: SxProps<Theme> }> = ({ id, children, sx }) => (
  <Box id={id} component="span" sx={{ fontSize: 13, color: C.muted, lineHeight: 1.45, ...(sx as object) }}>{children}</Box>
)
